import {
  Add,
  Checkbox,
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Input,
  TextArea,
  Trash,
  theme,
} from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { DeleteButtonText } from '../../../styledComponents'
import { Form as AntdForm, Form, FormInstance } from 'antd'
import { AllergySeverityCode } from '@lmc/ts-common'
import { FC } from 'react'

export const AllergiesFormCard: FC<{ form: FormInstance<any> }> = ({
  form,
}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const noKnownAllergies = AntdForm.useWatch('noKnownAllergies', form)

  const handleAddAllergy = () => {
    form.setFieldsValue({
      residentAllergies: [
        ...form.getFieldValue('residentAllergies'),
        ...(!noKnownAllergies ? [{}] : []),
      ],
      noKnownAllergies: false,
    })
  }

  return (
    <FormCard
      title={t(supConstants.ALLERGIES)}
      rightChildren={
        <Button variant="text" icon={<Add />} onClick={handleAddAllergy}>
          {t(supConstants.ADD_ALLERGY)}
        </Button>
      }
    >
      <FormItem name="noKnownAllergies" valuePropName="checked">
        <Checkbox>
          <Text>{t(supConstants.NO_KNOWN_ALLERGIES)}</Text>
        </Checkbox>
      </FormItem>
      {!noKnownAllergies && (
        <AntdForm.List name="residentAllergies">
          {(fields, { remove }) => (
            <>
              {fields.map((field) => (
                <Form.Item
                  key={field.key}
                  noStyle
                  shouldUpdate={(prevState, currentState) =>
                    prevState?.residentAllergies[field.name]?.causativeAgent !==
                    currentState?.residentAllergies[field.name]?.causativeAgent
                  }
                >
                  {({ getFieldValue }) => (
                    <FormCard
                      key={field.name}
                      level={2}
                      title={
                        getFieldValue([
                          'residentAllergies',
                          field.name,
                          'causativeAgent',
                        ]) ||
                        t(supConstants.ALLERGY_INDEX, {
                          variable: field.key + 1,
                        })
                      }
                      rightChildren={
                        <Button
                          variant="text"
                          icon={<Trash color={theme.color.neutral.color4} />}
                          onClick={() => remove(field.name)}
                        >
                          <DeleteButtonText weight="medium">
                            {t(supConstants.DELETE_ALLERGY)}
                          </DeleteButtonText>
                        </Button>
                      }
                    >
                      <FormItem
                        key={[
                          'residentAllergies',
                          field.name,
                          'causativeAgent',
                        ].join('-')}
                        label={<Text>{t(supConstants.CAUSATIVE_AGENT)}</Text>}
                        name={[field.name, 'causativeAgent']}
                        rules={[
                          {
                            required: true,
                            message: t(supConstants.CAUSATIVE_AGENT_ERROR),
                          },
                        ]}
                      >
                        <Input maxLength={50} />
                      </FormItem>
                      <FormItem
                        key={[
                          'residentAllergies',
                          field.name,
                          'typeOfReaction',
                        ].join('-')}
                        label={<Text>{t(supConstants.TYPE_OF_REACTION)}</Text>}
                        name={[field.name, 'typeOfReaction']}
                        rules={[
                          {
                            required: true,
                            message: t(supConstants.TYPE_OF_REACTION_ERROR),
                          },
                        ]}
                      >
                        <FormRadioGroup>
                          <FormRadio
                            value="allergy"
                            text={t(supConstants.ALLERGY_TYPE)}
                          />
                          <FormRadio
                            value="intolerance"
                            text={t(supConstants.INTOLERANCE)}
                          />
                        </FormRadioGroup>
                      </FormItem>
                      <FormItem
                        key={[
                          'residentAllergies',
                          field.name,
                          'severityCode',
                        ].join('-')}
                        label={
                          <Text>{t(supConstants.SEVERITY_OF_REACTION)}</Text>
                        }
                        name={[field.name, 'severityCode']}
                      >
                        <FormRadioGroup>
                          {Object.keys(AllergySeverityCode).map((key) => (
                            <FormRadio
                              value={
                                AllergySeverityCode[key as AllergySeverityCode]
                              }
                              key={key}
                              text={t(supConstants[key as AllergySeverityCode])}
                            />
                          ))}
                        </FormRadioGroup>
                      </FormItem>
                      <FormItem
                        key={[
                          'residentAllergies',
                          field.name,
                          'severityDescription',
                        ].join('-')}
                        label={<Text>{t(supConstants.DESCRIPTION)}</Text>}
                        name={[field.name, 'severityDescription']}
                      >
                        <TextArea
                          autoSize={{
                            minRows: 3,
                          }}
                        />
                      </FormItem>
                    </FormCard>
                  )}
                </Form.Item>
              ))}
            </>
          )}
        </AntdForm.List>
      )}
    </FormCard>
  )
}
